import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Send,
  Phone,
  Mail,
  ArrowRight,
  MapPin,
  ClipboardCheck,
} from "lucide-react";
import {
  Nav,
  Footer,
  usePageSeo,
  OFFICE_DISPLAY,
  OFFICE_TEL,
  WHATSAPP_DISPLAY,
  CONTACT_EMAIL,
  TRADING_ADDRESS_LINES,
  waLink,
} from "../layout/shared";

const SERVICE_OPTIONS = [
  {
    value: "agricultural",
    label: "Farm plastic / agricultural recycling",
    fields: [
      { name: "materialType", label: "Material type", placeholder: "e.g. silage wrap, feed sacks, mixed" },
      { name: "quantity", label: "Approximate quantity", placeholder: "e.g. 4 bulk bags of wrap" },
    ],
  },
  {
    value: "cardboard",
    label: "Cardboard collection",
    fields: [
      { name: "quantity", label: "Approximate volume", placeholder: "e.g. 2 pallets flat, 6 cages" },
      { name: "frequency", label: "One-off or scheduled?", placeholder: "one-off / weekly / monthly" },
    ],
  },
  {
    value: "polystyrene",
    label: "Rigid plastics & polystyrene / EPS",
    fields: [
      { name: "materialType", label: "Plastic type / description", placeholder: "e.g. HDPE drums, crates or clean EPS" },
      { name: "quantity", label: "Approximate volume", placeholder: "e.g. one pallet, one cage" },
      { name: "previousUse", label: "What was it used for?", placeholder: "Previous contents or use" },
      { name: "storage", label: "How is it stored?", placeholder: "Loose / bagged / stacked / baled" },
      { name: "frequency", label: "How regularly is it produced?", placeholder: "One-off / weekly / monthly" },
    ],
  },
  {
    value: "secure",
    label: "Secure destruction",
    fields: [
      { name: "materialClass", label: "Material class", placeholder: "Documents / branded stock / uniforms / product" },
      { name: "sensitivity", label: "Outcome required", placeholder: "e.g. certificate of destruction needed for audit" },
    ],
  },
  {
    value: "reservoir",
    label: "Farm reservoir project",
    fields: [
      { name: "capacity", label: "Rough intended capacity", placeholder: "e.g. 10,000 m³ irrigation reservoir" },
      { name: "timeline", label: "Target completion", placeholder: "e.g. autumn 2027" },
    ],
  },
  {
    value: "aggregates",
    label: "Soil, hardcore or mobile processing",
    fields: [
      { name: "materialOrigin", label: "Material origin & type", placeholder: "e.g. concrete arisings from a site strip" },
      { name: "quantity", label: "Approximate tonnage", placeholder: "e.g. ~300 tonnes" },
    ],
  },
  {
    value: "other",
    label: "Something else",
    fields: [
      { name: "materialType", label: "Material or project", placeholder: "Tell us what you have in mind" },
    ],
  },
];

const CORE_FIELDS = [
  { name: "name", label: "Your name", required: true, placeholder: "First and last" },
  { name: "business", label: "Farm or business", required: true, placeholder: "e.g. Mill Farm Ltd" },
  { name: "postcode", label: "Site postcode", required: true, placeholder: "e.g. WR15 8JJ" },
  { name: "contact", label: "Best contact number or email", required: true, placeholder: "For call-back or quote" },
];

const inputCls =
  "w-full min-w-0 bg-white border border-[#024120]/15 px-4 py-3 text-[15px] text-[#024120] placeholder:text-[#4A4A4A] focus:border-[#024120] outline-none transition-colors";

const GetAQuote = () => {
  usePageSeo({
    title: "Get a Recycling or Project Quote · 3 Counties Recycling",
    description:
      "Send your material, site, quantity and postcode for a clear assessment and quotation from 3 Counties Recycling.",
    path: "/get-a-quote",
  });

  const [searchParams] = useSearchParams();
  const requestedService = searchParams.get("service");
  const [service, setService] = useState(() => SERVICE_OPTIONS.some((s) => s.value === requestedService) ? requestedService : "");
  const [core, setCore] = useState({
    name: "",
    business: "",
    postcode: "",
    contact: "",
    notes: "",
  });
  const [extra, setExtra] = useState({});
  const [draftUrl, setDraftUrl] = useState("");

  useEffect(() => {
    setService(SERVICE_OPTIONS.some((s) => s.value === requestedService) ? requestedService : "");
    setExtra({});
    setDraftUrl("");
  }, [requestedService]);

  const selected = SERVICE_OPTIONS.find((s) => s.value === service);

  const setCoreField = (name, v) => setCore((c) => ({ ...c, [name]: v }));
  const setExtraField = (name, v) => setExtra((e) => ({ ...e, [name]: v }));

  const onSubmit = (e) => {
    e.preventDefault();
    const svcLabel = selected ? selected.label : "-";
    const lines = [
      "Hi 3 Counties Recycling, I would like a quote.",
      "",
      `Service: ${svcLabel}`,
      `Name: ${core.name || "-"}`,
      `Farm / business: ${core.business || "-"}`,
      `Site postcode: ${core.postcode || "-"}`,
      `Contact: ${core.contact || "-"}`,
    ];
    if (selected) {
      selected.fields.forEach((f) => {
        lines.push(`${f.label}: ${extra[f.name] || "-"}`);
      });
    }
    if (core.notes) {
      lines.push("", `Notes: ${core.notes}`);
    }
    lines.push(
      "",
      "I understand nothing moves until 3 Counties Recycling has confirmed the route in writing."
    );
    const msg = lines.join("\n");
    const url = waLink(msg);
    setDraftUrl(url);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <main data-testid="quote-root" className="bg-white text-[#1a1a1a]">
      <Nav />

      <section className="relative overflow-hidden bg-[#024120] text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-[#024120] via-[#024120] to-[#012b14]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-28 pb-16">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#FF6600]">
            Quote · assessment · next step
          </p>
          <h1
            data-testid="quote-h1"
            className="font-display font-black text-4xl md:text-5xl lg:text-[60px] leading-[1.05] tracking-tight mt-5 max-w-4xl"
          >
            Send us the details.{" "}
            <span className="text-[#46B649]">
              We will confirm the route before anything moves.
            </span>
          </h1>
          <p className="mt-6 max-w-3xl text-base md:text-lg text-white/85 leading-relaxed">
            Tell us what you have, where it is and what needs to happen next.
            Send the postcode, photographs, approximate quantity and required
            date. We will confirm whether we can help, what information is
            still needed and the next step.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
          {/* Contact sidebar */}
          <div className="lg:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
              / Other ways to reach us
            </p>
            <h2 className="mt-3 font-display font-black text-2xl md:text-3xl tracking-tight text-[#024120] leading-[1.1]">
              Prefer to talk? Ring or WhatsApp us instead.
            </h2>

            <div className="mt-8 space-y-4">
              <a
                href={`tel:${OFFICE_TEL}`}
                data-testid="quote-phone-link"
                className="quote-contact-link group flex items-center justify-between gap-6 bg-[#F2F2F2] px-5 py-5 hover:bg-[#024120] hover:text-white transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center bg-[#FF6600] text-white">
                    <Phone className="w-5 h-5" strokeWidth={1.8} />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-[#4A4A4A] group-hover:text-white/60">
                      Call us
                    </p>
                    <p className="font-display font-bold tabular-nums whitespace-nowrap text-xl mt-0.5">
                      {OFFICE_DISPLAY}
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>

              <a
                href={waLink(
                  "Hi 3 Counties Recycling, I would like a quote. Please can we chat?"
                )}
                target="_blank"
                rel="noreferrer"
                data-testid="quote-whatsapp-link"
                className="quote-contact-link group flex items-center justify-between gap-6 bg-[#F2F2F2] px-5 py-5 hover:bg-[#024120] hover:text-white transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center bg-[#46B649] text-white">
                    <Send className="w-5 h-5" strokeWidth={1.8} />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-[#4A4A4A] group-hover:text-white/60">
                      WhatsApp or text
                    </p>
                    <p className="font-display font-bold tabular-nums whitespace-nowrap text-xl mt-0.5">
                      {WHATSAPP_DISPLAY}
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>

              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="quote-email-link"
                className="quote-contact-link group flex items-center justify-between gap-6 bg-[#F2F2F2] px-5 py-5 hover:bg-[#024120] hover:text-white transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center bg-[#024120] text-white group-hover:bg-white/10">
                    <Mail className="w-5 h-5" strokeWidth={1.8} />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-[#4A4A4A] group-hover:text-white/60">
                      Email
                    </p>
                    <p className="font-semibold text-base mt-0.5">
                      {CONTACT_EMAIL}
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>

            <div className="mt-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#46B649] flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Trading contact address
              </p>
              <address data-testid="quote-trading-address" className="mt-3 not-italic text-[#024120] text-[15px] leading-relaxed">
                {TRADING_ADDRESS_LINES.map((l) => (
                  <span key={l} className="block">
                    {l}
                  </span>
                ))}
              </address>
              <p className="mt-3 text-xs text-[#4A4A4A]">
                Visits by prior arrangement only. Do not deliver material
                unless the receiving location, material and booking have
                been confirmed in writing.
              </p>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            onChange={() => setDraftUrl("")}
            data-testid="quote-form"
            className="lg:col-span-8 min-w-0 bg-[#F2F2F2] p-5 sm:p-8 md:p-10"
          >
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
              / Tell us about the job
            </p>
            <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight text-[#024120] mt-2">
              Request a quote for your material or project.
            </h2>

            {/* Service selector */}
            <div className="mt-8">
              <label className="block">
                <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#024120]">
                  Service required
                </span>
                <select
                  data-testid="quote-service-select"
                  required
                  value={service}
                  onChange={(e) => {
                    setService(e.target.value);
                    setExtra({});
                  }}
                  className={`${inputCls} mt-2`}
                >
                  <option value="">Select the service...</option>
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Core fields */}
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {CORE_FIELDS.map((f) => (
                <label key={f.name} className="block">
                  <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#024120]">
                    {f.label}
                  </span>
                  <input
                    data-testid={`quote-${f.name}`}
                    required={f.required}
                    value={core[f.name]}
                    onChange={(e) => setCoreField(f.name, e.target.value)}
                    className={`${inputCls} mt-2`}
                    placeholder={f.placeholder}
                  />
                </label>
              ))}
            </div>

            {/* Service-specific fields */}
            {selected && (
              <div
                data-testid="quote-service-fields"
                className="mt-6 grid sm:grid-cols-2 gap-4 rise"
              >
                {selected.fields.map((f) => (
                  <label
                    key={f.name}
                    className={`block ${
                      selected.fields.length === 1 ? "sm:col-span-2" : ""
                    }`}
                  >
                    <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#024120]">
                      {f.label}
                    </span>
                    <input
                      data-testid={`quote-extra-${f.name}`}
                      value={extra[f.name] || ""}
                      onChange={(e) => setExtraField(f.name, e.target.value)}
                      className={`${inputCls} mt-2`}
                      placeholder={f.placeholder}
                    />
                  </label>
                ))}
              </div>
            )}

            {/* Notes */}
            <label className="block mt-6">
              <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#024120]">
                Notes{" "}
                <span className="text-[#4A4A4A] normal-case tracking-normal font-normal">
                  (optional)
                </span>
              </span>
              <textarea
                data-testid="quote-notes"
                rows={4}
                value={core.notes}
                onChange={(e) => setCoreField("notes", e.target.value)}
                className={`${inputCls} mt-2 resize-none`}
                placeholder="Access notes, photos to follow, previous analysis, any deadlines..."
              />
            </label>

            <button
              type="submit"
              data-testid="quote-submit-button"
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#FF6600] hover:bg-[#E55A00] text-white font-bold px-8 py-4 transition-all hover:-translate-y-1"
            >
              <Send className="w-5 h-5" /> Send via WhatsApp{" "}
              <ArrowRight className="w-4 h-4" />
            </button>

            <p data-testid="quote-submission-status" role="status" className="mt-4 flex items-start gap-2 text-xs text-[#4A4A4A] leading-relaxed">
              <ClipboardCheck className="w-4 h-4 text-[#46B649] shrink-0 mt-0.5" />
              {draftUrl
                ? "Your enquiry is ready. Send it in WhatsApp to contact us, and attach any photographs there. No message has been sent by this website."
                : "Submitting opens WhatsApp with the details pre-filled. Nothing moves until we have confirmed the route in writing. See our privacy notice for how we handle enquiry information."}
            </p>
            {draftUrl && <a data-testid="quote-open-whatsapp-draft" href={draftUrl} target="_blank" rel="noreferrer" className="inline-block mt-4 text-sm font-bold underline underline-offset-4 text-[#024120]">Open your WhatsApp enquiry</a>}
          </form>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default GetAQuote;
